import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddCommentLikesMutation } from '@ports/internal-services/commentLikes/commentLikesSlice';
import { SVGAssets } from '@business/assets/vectors';
import {
  CommentItemProps,
  CommentSectionProps
} from '@business/interfaces/photo';
import { Avatar } from '@business/workflows/ComponentUI/Avatar';
import { useSelector } from 'react-redux';
import { getUserData } from '@ports/common/userStateSlice';
import { Button, Input } from '@business/workflows/ComponentUI';
import { getRelativeTime } from '@utils/helpers/lib/photoHelpers';

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  currentUserId,
  onDeleteComment,
  onUpdateComment,
  onCommentLikeUpdate
}) => {
  const { t } = useTranslation();
  // Mutations
  const [addCommentLike] = useAddCommentLikesMutation();

  // Local state for optimistic updates
  const [isLiked, setIsLiked] = useState(comment.isLiked || false);
  const [likeCount, setLikeCount] = useState(comment.commentLikeCount || 0);
  const [userLikeId, setUserLikeId] = useState<string | null>(
    comment.userCommentLikeId || null
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);

  // Update state when comment data changes
  useEffect(() => {
    // Check if current user has liked by looking in commentLikes array
    const userHasLiked =
      comment.commentLikes?.some(like => like.userId === currentUserId) ||
      false;
    const userLike = comment.commentLikes?.find(
      like => like.userId === currentUserId
    );

    setIsLiked(userHasLiked);
    setLikeCount(comment.commentLikesCount || comment.commentLikeCount || 0);
    setUserLikeId(userLike?.id || null);
  }, [comment, currentUserId]);

  const handleCommentLike = () => {
    if (isProcessing || !currentUserId) return;
    setIsProcessing(true);

    // Store previous state for rollback
    const previousIsLiked = isLiked;
    const previousCount = likeCount;
    const previousLikeId = userLikeId;

    // 1. OPTIMISTIC UPDATE - Update UI immediately (no try-catch here)
    setIsLiked(!isLiked);
    setLikeCount(prev => (isLiked ? prev - 1 : prev + 1));

    // 2. API CALL in background - fire and forget
    addCommentLike({
      createCommentLikes: {
        commentId: comment.id,
        userId: currentUserId
      }
    })
      .unwrap()
      .then(result => {
        // SUCCESS - Update with real data
        if (result.data?.rows?.[0]?.id) {
          setUserLikeId(result.data.rows[0].id);
        }

        // Call parent callback to refetch comments
        setTimeout(() => {
          onCommentLikeUpdate?.(comment.id, isLiked, userLikeId);
        }, 100);
      })
      .catch(error => {
        console.error('Failed to toggle comment like:', error);

        // ROLLBACK on failure
        setIsLiked(previousIsLiked);
        setLikeCount(previousCount);
        setUserLikeId(previousLikeId);
      })
      .finally(() => {
        setTimeout(() => setIsProcessing(false), 300);
      });
  };

  const handleEdit = () => {
    setIsEditing(true);
    setShowDropdown(false);
  };

  const handleSaveEdit = () => {
    if (editText.trim() && onUpdateComment) {
      onUpdateComment(comment.id, editText.trim());
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditText(comment.text);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (onDeleteComment) {
      onDeleteComment(comment.id);
    }
    setShowDropdown(false);
  };

  return (
    <article className="bg-[var(--media-comment-bg)] rounded-md px-4 py-3 flex gap-3">
      <div className="w-9 h-9 rounded-full bg-gray-500 flex items-center justify-center text-sm font-semibold flex-shrink-0 overflow-hidden">
        <Avatar
          alt={comment.name}
          imgClassName="rounded-full w-12 h-12 border border-gray-300 mb-2"
          src={comment.profileImage}
          size="h-12 w-12"
          isHeader={true}
        />
      </div>

      <div className="flex-1">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-white">{comment.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-[11px] text-gray-400">
              {getRelativeTime(comment.timestamp)}
            </p>
            {comment.userId === currentUserId && (
              <div className="relative">
                <Button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/10"
                  btnStyle="no-outline"
                  aria-label="More options">
                  <span className="w-1 h-1 rounded-full bg-gray-300" />
                  <span className="w-1 h-1 rounded-full bg-gray-300 mx-0.5" />
                  <span className="w-1 h-1 rounded-full bg-gray-300" />
                </Button>
                {showDropdown && (
                  <div className="absolute right-0 mt-1 w-32 bg-[#2a3520] rounded-md shadow-lg z-10 overflow-hidden">
                    <Button
                      onClick={handleEdit}
                      className="w-full px-4 py-2 text-left text-sm text-gray-200 hover:bg-white/10 transition-colors"
                      btnStyle="no-outline">
                      {t('mediaView.edit')}
                    </Button>
                    <Button
                      onClick={handleDelete}
                      className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-white/10 transition-colors"
                      btnStyle="no-outline">
                      {t('mediaView.delete')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {isEditing ? (
          <div className="mt-2">
            <Input
              type="text"
              value={editText}
              onChange={e => setEditText(e.target.value)}
              className="w-full bg-[var(--media-input-bg)] rounded px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              autoFocus
            />
            <div className="flex gap-2 mt-2">
              <Button
                onClick={handleSaveEdit}
                className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                btnStyle="no-outline">
                {t('submit')}
              </Button>
              <Button
                onClick={handleCancelEdit}
                className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                btnStyle="no-outline">
                {t('cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <p className="mt-2 text-sm text-gray-100">{comment.text}</p>
        )}

        <div className="flex items-center gap-3 mt-2">
          <span
            className="flex items-center space-x-1 cursor-pointer"
            onClick={handleCommentLike}>
            {isLiked ? (
              <SVGAssets.LikeRed className="w-4 h-4" />
            ) : (
              <SVGAssets.LikeIcons className="w-4 h-4" />
            )}
            <span className="text-xs text-gray-400">{likeCount || 0}</span>
          </span>
        </div>
      </div>
    </article>
  );
};

const CommentSection: React.FC<CommentSectionProps> = ({
  comments = [],
  currentUserId,
  onAddComment,
  onDeleteComment,
  onUpdateComment,
  onCommentLikeUpdate,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const [commentText, setCommentText] = useState('');
  const userDetails = useSelector(getUserData);
  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (commentText.trim() && onAddComment) {
      onAddComment(commentText);
      setCommentText('');
    }
  };

  return (
    <section className="mt-4 space-y-3">
      {/* Comment input */}
      <div className="flex items-start gap-3 bg-[var(--media-comment-bg)] rounded-md px-4 py-3">
        <Avatar
          alt={userDetails?.fullName}
          imgClassName="w-12 h-12 border border-gray-300 mb-2"
          src={userDetails?.profileImageUrl}
          size="h-12 w-12"
          isHeader={true}
          rounded={true}
        />
        <form
          onSubmit={handleSubmit}
          className="flex-1 flex gap-2 items-center">
          <Input
            type="text"
            value={commentText}
            onChange={e => setCommentText(e.target.value)}
            placeholder={t('mediaView.writeComment')}
            disabled={isLoading}
            className="flex-1 bg-[var(--media-input-bg)] rounded-full px-4 py-2 text-sm text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
          />
          <Button
            type="submit"
            btnStyle="no-outline"
            disabled={!commentText.trim() || isLoading}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-full hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <SVGAssets.Send className="w-4 h-4" />
          </Button>
        </form>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-4 text-gray-400 text-sm">
          {t('mediaView.loadingComments')}
        </div>
      )}

      {/* Comments list */}
      {comments.map(comment => (
        <CommentItem
          key={comment.id}
          comment={comment}
          currentUserId={currentUserId}
          onDeleteComment={onDeleteComment}
          onUpdateComment={onUpdateComment}
          onCommentLikeUpdate={onCommentLikeUpdate}
        />
      ))}
    </section>
  );
};

export default CommentSection;
